<template>
  <div id="app-root">
    <!-- Marketing pages (referral landing) render in a blank layout: no app shell,
         no PullToRefresh transform wrapper — the landing needs body scroll plus
         working position:sticky/fixed for its scroll-driven animations. -->
    <TmaInitPage v-if="tmaPending" />
    <RouterView v-else-if="!sponsorRestricted && isBlankLayout && !backgroundRoute" />
    <AppLayout v-else-if="!sponsorRestricted" :route="backgroundRoute ?? undefined" />
    <ProductLandingOverlay
      v-if="backgroundRoute && !sponsorRestricted && !tmaPending"
      :key="route.path"
      :label="t(String(route.meta.title))"
      :close-label="t('aiStudio.backToDashboard')"
      @close="router.back()"
    >
      <RouterView v-slot="{ Component }">
        <component :is="Component" embedded />
      </RouterView>
    </ProductLandingOverlay>
    <CaptchaPopup />
    <SponsorRequiredModal
      v-if="!tmaPending && profileStore.needsSponsor && profileStore.sponsorPromptOpen"
      @profile="openProfile"
      @saved="profileStore.sponsorPromptOpen = false"
    />
    <MascotLoader v-if="!tmaPending && isBlankLayout && !backgroundRoute" :active="layoutStore.isNavigating" :label="t('common.loading')" />
    <EmailLinkReminder :eligible="emailReminderEligible" />
    <BuildUpdateNotice />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, shallowRef, watch, type Ref } from 'vue'
import type { TmaState } from '@/mini-app'
import TmaInitPage from '@/mini-app/TmaInitPage.vue'
import type { TmaAuthFlow } from '@/mini-app/tmaAuthFlow'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import BuildUpdateNotice from '@/components/common/BuildUpdateNotice.vue'
import ProductLandingOverlay from '@/components/common/ProductLandingOverlay.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import CaptchaPopup from '@/shared/components/CaptchaPopup.vue'
import MascotLoader from '@/components/common/MascotLoader.vue'
import { useLayoutStore } from '@/store/layoutStore'
import { EmailLinkReminder, useProfileStore } from '@/modules/profile'
import SponsorRequiredModal from '@/modules/profile/components/SponsorRequiredModal.vue'
import { sponsorRouteRedirect } from '@/modules/profile/services/sponsorAccess'

const route = useRoute()
const router = useRouter()
const layoutStore = useLayoutStore()
const { t } = useI18n()
const profileStore = useProfileStore()
const tmaState = inject<Ref<TmaState> | undefined>('tmaState', undefined)
const isInTelegram = inject<boolean>('isInTelegram', false)
const tmaAuthFlow = inject<TmaAuthFlow>('tmaAuthFlow')
const tmaPending = computed(() => isInTelegram && !tmaState?.value.abortedToLanding && tmaState?.value.status !== 'done')
watch(() => tmaState?.value.status, status => {
  if (isInTelegram && status === 'done') void router.replace('/dashboard')
})
const emailReminderEligible = computed(() => isInTelegram && tmaState?.value.status === 'done' && !tmaAuthFlow?.skippedEmail.value
  && !!profileStore.profile && profileStore.hasCountry && !profileStore.needsSponsor
  && !profileStore.profile.security?.password_change_required)
const sponsorRestricted = computed(() => !!sponsorRouteRedirect(profileStore.profile, route.name))
// Also react to base-info refreshes while a previously accessible page is open.
watch(sponsorRestricted, restricted => {
  if (restricted) {
    profileStore.sponsorPromptOpen = true
    void router.replace({ name: 'profile' })
  }
}, { immediate: true })

function openProfile() {
  profileStore.sponsorPromptOpen = false
  void router.replace({ name: 'profile' })
}
// Memory-only: reloading a product URL deliberately opens a standalone landing.
const backgroundRoute = shallowRef<RouteLocationNormalizedLoaded | null>(null)
watch(router.currentRoute, (to, from) => {
  if (!to.meta.productLanding) {
    backgroundRoute.value = null
  } else if (!backgroundRoute.value && (from.name === 'dashboard' || from.name === 'dashboard-prestart')) {
    backgroundRoute.value = from
  }
}, { flush: 'sync' })
const isBlankLayout = computed(() => route.meta?.layout === 'blank')
</script>
